import * as React from 'react';
import { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { EnterpriseSetting_Key, EnterpriseSetting_ManagedLevelScopeType } from '__generated__/graphql-types';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { InlineNotification, LoadingSection, Radio, RadioGroup } from '@coursera/cds-core';

import EnterpriseSettingFindByScopeAndKey from 'bundles/enterprise-admin-users/queries/EnterpriseSettingFindByScopeAndKey.graphql';
import type {
  EnterpriseSettingFindByScopeAndKeyQuery,
  EnterpriseSettingFindByScopeAndKeyQueryVariables,
} from 'bundles/enterprise-admin-users/queries/__generated__/EnterpriseSettingFindByScopeAndKey';
import EnterpriseSettingUpdate from 'bundles/internal-enterprise/components/queries/EnterpriseSettingUpdate.graphql';
import type {
  EnterpriseSettingUpdateMutation,
  EnterpriseSettingUpdateMutationVariables,
} from 'bundles/internal-enterprise/components/queries/__generated__/EnterpriseSettingUpdate';

import _t from 'i18n!nls/internal-enterprise';

type Props = {
  thirdPartyOrganizationId: string;
};

const PiiValues = {
  SHOW: 'show',
  HIDE: 'hide',
} as const;

type PiiValue = (typeof PiiValues)[keyof typeof PiiValues];

const PiiRadio: React.FC<Props> = ({ thirdPartyOrganizationId }) => {
  const [piiValue, setPiiValue] = useState<PiiValue>(PiiValues.SHOW);
  const [updating, setUpdating] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [updateMutation] = useMutation<EnterpriseSettingUpdateMutation, EnterpriseSettingUpdateMutationVariables>(
    EnterpriseSettingUpdate,
    {
      context: {
        clientName: 'gatewayGql',
      },
    }
  );

  const { data, loading } = useQuery<
    EnterpriseSettingFindByScopeAndKeyQuery,
    EnterpriseSettingFindByScopeAndKeyQueryVariables
  >(EnterpriseSettingFindByScopeAndKey, {
    variables: {
      managedLevelScopeType: EnterpriseSetting_ManagedLevelScopeType.ThirdPartyOrganization,
      managedLevelScopeValue: thirdPartyOrganizationId,
      enterpriseSettingKey: EnterpriseSetting_Key.EnterpriseSettingKeyRemovedMemberAnonymizationEnabled,
    },
    errorPolicy: 'all',
    context: { clientName: 'gatewayGql' },
  });

  useEffect(() => {
    if (data) {
      let anonymizationEnabled = false;
      try {
        const parsedData = JSON.parse(data?.EnterpriseSetting?.findByScopeAndKey?.data);
        anonymizationEnabled = parsedData[EnterpriseSetting_Key.EnterpriseSettingKeyRemovedMemberAnonymizationEnabled];
      } catch {
        anonymizationEnabled = false;
      }

      setPiiValue(anonymizationEnabled ? PiiValues.HIDE : PiiValues.SHOW);
    }
  }, [data]);

  if (loading) {
    return <LoadingSection />;
  }

  const onPiiRadioChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setUpdating(true);
    const newValue = event.target.value as PiiValue;
    try {
      const { data: updateData, errors: updateErrors } = await updateMutation({
        variables: {
          enterpriseSettingType: {
            booleanEnterpriseSetting: newValue === PiiValues.HIDE,
          },
          organizationId: thirdPartyOrganizationId,
          settingId: {
            managedLevelScope: {
              scope: EnterpriseSetting_ManagedLevelScopeType.ThirdPartyOrganization,
              value: thirdPartyOrganizationId,
            },
            enterpriseSettingKey: EnterpriseSetting_Key.EnterpriseSettingKeyRemovedMemberAnonymizationEnabled,
          },
        },
      });
      if (updateData) {
        setPiiValue(newValue);
      } else if (updateErrors) {
        const message = updateErrors[0]?.message;
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        setErrorMessage(message);
        setShowErrorMessage(true);
      }
    } catch {
      setErrorMessage('There was an error updating the setting. Please try again later.');
      setShowErrorMessage(true);
    }
    setUpdating(false);
  };

  return (
    <React.Fragment>
      {showErrorMessage && errorMessage && (
        <InlineNotification severity="error" onDismiss={() => setShowErrorMessage(false)}>
          <FormattedMessage message={_t('Error updating: {errorMessage}')} errorMessage={errorMessage} />
        </InlineNotification>
      )}
      <RadioGroup label={_t('Allow PII for terminated employees')} onChange={onPiiRadioChange} value={piiValue}>
        <Radio
          label={_t('Show Personal Identifiable Information (PII) for terminated employees')}
          value={PiiValues.SHOW}
          disabled={updating}
        />
        <Radio
          label={_t('Redact Personal Identifiable Information (PII) for terminated employees')}
          value={PiiValues.HIDE}
          disabled={updating}
        />
      </RadioGroup>
    </React.Fragment>
  );
};

export default PiiRadio;
